import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../hooks/ThemeContext';
import { UserContext } from '../hooks/UserContext';
import ContactForm from '../components/common/ContactForm';
import SectionTitle from '../components/common/SectionTitle';
import { User, Mail, MessageCircle, Sparkles } from 'lucide-react';

const Contact = () => {
  const { theme } = useContext(ThemeContext);
  const { isAuthenticated } = useContext(UserContext);
  const isDark = theme === 'dark';

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        
        <SectionTitle 
          title="Nous Contacter"
          subtitle={
            isAuthenticated 
              ? 'Choisissez qui vous souhaitez contacter selon vos besoins' 
              : 'Contactez notre équipe pour toute question ou demande'
          }
          align="center"
        />

        {isAuthenticated ? (
          /* Options de contact pour utilisateurs connectés */
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Contact Admin */}
              <div 
                className={`group relative overflow-hidden p-8 rounded-2xl border transition-all duration-300 hover:scale-[1.02] ${
                  isDark 
                    ? 'bg-gradient-to-br from-rose-950/30 to-orange-950/20 border-rose-500/20 hover:border-rose-500/40 hover:shadow-xl hover:shadow-rose-500/10' 
                    : 'bg-gradient-to-br from-rose-50 to-orange-50 border-rose-200 hover:border-rose-300 hover:shadow-xl hover:shadow-rose-200/50'
                }`}
              >
                {/* Icône animée */}
                <div className="relative mb-6">
                  <div className={`inline-flex p-4 rounded-2xl transition-transform duration-300 group-hover:scale-110 ${
                    isDark ? 'bg-rose-500/20' : 'bg-rose-100'
                  }`}>
                    <Mail className={`w-8 h-8 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
                  </div>
                  <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full animate-pulse ${
                    isDark ? 'bg-rose-500' : 'bg-rose-400'
                  }`} />
                </div>

                {/* Contenu */}
                <h3 className="text-2xl font-bold mb-3 flex items-center gap-2">
                  Support & Administration
                  <MessageCircle className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
                </h3>
                
                <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Questions générales, support technique, demandes administratives, ou aide avec votre compte.
                </p>

                <div className={`flex flex-col gap-2 mb-6 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-rose-400' : 'bg-rose-600'}`} />
                    Réponse sous 24h
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-rose-400' : 'bg-rose-600'}`} />
                    Support prioritaire
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-rose-400' : 'bg-rose-600'}`} />
                    Aide personnalisée
                  </div>
                </div>

                <div className={`px-4 py-3 rounded-xl text-sm font-medium ${
                  isDark ? 'bg-white/5 text-gray-300' : 'bg-white text-gray-700'
                }`}>
                  ↓ Formulaire ci-dessous
                </div>
              </div>

              {/* Contact Créateur */}
              <div 
                className={`group relative overflow-hidden p-8 rounded-2xl border transition-all duration-300 hover:scale-[1.02] ${
                  isDark 
                    ? 'bg-gradient-to-br from-purple-950/30 via-blue-950/20 to-indigo-950/20 border-purple-500/20 hover:border-purple-500/40 hover:shadow-xl hover:shadow-purple-500/10' 
                    : 'bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 border-purple-200 hover:border-purple-300 hover:shadow-xl hover:shadow-purple-200/50'
                }`}
              >
                {/* Particules décoratives */}
                <div className="absolute top-4 right-4 opacity-20">
                  <Sparkles className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>

                {/* Icône animée */}
                <div className="relative mb-6">
                  <div className={`inline-flex p-4 rounded-2xl transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6 ${
                    isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                  }`}>
                    <User className={`w-8 h-8 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  </div>
                </div>

                {/* Contenu */}
                <h3 className="text-2xl font-bold mb-3 flex items-center gap-2">
                  Projets & Créations
                  <Sparkles className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                </h3>
                
                <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Commandes personnalisées, projets sur-mesure, collaborations créatives, ou discussions avec nos artistes.
                </p>

                <div className={`flex flex-col gap-2 mb-6 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`} />
                    Échange direct avec le créateur
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`} />
                    Devis personnalisé
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-purple-400' : 'bg-purple-600'}`} />
                    Projets sur-mesure
                  </div>
                </div>

                <Link 
                  to="/contacter-createur"
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 group-hover:gap-3 ${
                    isDark 
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white shadow-lg shadow-purple-500/30' 
                      : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white shadow-lg shadow-purple-500/30'
                  }`}
                >
                  Choisir un créateur
                  <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </>
        ) : (
          /* Utilisateur déconnecté - Seulement contact admin */
          <div className="max-w-3xl mx-auto mb-12">
            <div 
              className={`p-8 rounded-2xl border ${
                isDark 
                  ? 'bg-gradient-to-br from-rose-950/30 to-orange-950/20 border-rose-500/20' 
                  : 'bg-gradient-to-br from-rose-50 to-orange-50 border-rose-200'
              }`}
            >
              <div className={`inline-flex p-4 rounded-2xl mb-6 ${
                isDark ? 'bg-rose-500/20' : 'bg-rose-100'
              }`}>
                <Mail className={`w-8 h-8 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
              </div>
              
              <h3 className="text-2xl font-bold mb-3">Contactez notre équipe</h3>
              <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Envoyez-nous votre message via le formulaire ci-dessous. Notre équipe vous répondra dans les plus brefs délais.
              </p>

              <div className={`px-4 py-3 rounded-xl border ${
                isDark 
                  ? 'bg-blue-500/10 border-blue-500/20 text-blue-400' 
                  : 'bg-blue-50 border-blue-200 text-blue-700'
              }`}>
                <p className="text-sm font-medium">
                  💡 Connectez-vous pour accéder à plus d'options de contact et contacter directement nos créateurs
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Formulaire de contact */}
        <ContactForm variant="default" />

        {!isAuthenticated && (
          <div className={`mt-8 p-6 rounded-xl border ${
            isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
          }`}>
            <p className="text-center">
              Vous avez déjà un compte ?{' '}
              <Link to="/login" className={`font-semibold hover:underline ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>
                Connectez-vous
              </Link>
              {' '}pour un support prioritaire et plus d'options de contact.
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default Contact;
